import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Spinner,
  Alert
} from 'reactstrap';
import { Icon, RSelect } from '../Component';
import {
  EXPORT_FORMATS,
  RANKING_OPTIONS,
  PRESENTATION_THEMES,
  generateFilename
} from '../../utils/export/exportUtils';

const ExportModal = ({
  isOpen,
  toggle,
  onExport,
  isLoading = false,
  error = null,
  agentData = [],
  currentRanking = 'TOTAL_POINTS'
}) => {
  const [exportConfig, setExportConfig] = useState({
    format: EXPORT_FORMATS.PDF,
    ranking: currentRanking,
    theme: 'PROFESSIONAL',
    includeCharts: true,
    includeTopPerformers: true,
    includeFullLeaderboard: true,
    includeTeamPerformance: true,
    includeLeadFunnel: true,
    topCount: 10,
    customTitle: '',
    orientation: 'landscape',
    presentationStyle: 'full' // 'full' or 'compact'
  });

  const formatOptions = [
    { value: EXPORT_FORMATS.PDF, label: 'PDF Document' },
    { value: EXPORT_FORMATS.PNG, label: 'PNG Image' },
    { value: EXPORT_FORMATS.JPEG, label: 'JPEG Image' }
  ];

  const rankingOptions = Object.entries(RANKING_OPTIONS).map(([key, option]) => ({
    value: key,
    label: option.label
  }));

  const themeOptions = Object.entries(PRESENTATION_THEMES).map(([key, theme]) => ({
    value: key,
    label: theme.name
  }));

  const orientationOptions = [
    { value: 'landscape', label: 'Landscape' },
    { value: 'portrait', label: 'Portrait' }
  ];

  const topCountOptions = [
    { value: 5, label: 'Top 5' },
    { value: 10, label: 'Top 10' },
    { value: 15, label: 'Top 15' },
    { value: 20, label: 'Top 20' },
    { value: 25, label: 'Top 25' }
  ];

  const presentationStyleOptions = [
    { value: 'full', label: 'Full Report (Detailed)' },
    { value: 'compact', label: 'Compact View (Summary)' }
  ];

  const handleConfigChange = (field, value) => {
    setExportConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExport = () => {
    const filename = generateFilename(
      exportConfig.customTitle || 'Agent_Leaderboard_Report',
      exportConfig.ranking
    );

    onExport({
      ...exportConfig,
      filename
    });
  };

  const getPreviewData = () => {
    const rankingOption = RANKING_OPTIONS[exportConfig.ranking];
    const theme = PRESENTATION_THEMES[exportConfig.theme];
    const topAgents = agentData.slice(0, exportConfig.topCount);

    return {
      rankingOption,
      theme,
      topAgents,
      totalAgents: agentData.length
    };
  };

  const { rankingOption, theme, topAgents, totalAgents } = getPreviewData();

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
      <ModalHeader toggle={toggle} className="border-bottom">
        <Icon name="download" className="me-2" />
        Export Agent Leaderboard
      </ModalHeader>

      <ModalBody className="p-4">
        {error && (
          <Alert color="danger" className="mb-4">
            <Icon name="alert-circle" className="me-2" />
            {error}
          </Alert>
        )}

        <Row>
          {/* Configuration Panel */}
          <Col lg="8">
            <Form>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Export Format</Label>
                    <RSelect
                      options={formatOptions}
                      value={formatOptions.find(opt => opt.value === exportConfig.format)}
                      onChange={(option) => handleConfigChange('format', option.value)}
                      placeholder="Select format..."
                    />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>Ranking Criteria</Label>
                    <RSelect
                      options={rankingOptions}
                      value={rankingOptions.find(opt => opt.value === exportConfig.ranking)}
                      onChange={(option) => handleConfigChange('ranking', option.value)}
                      placeholder="Select ranking..."
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Presentation Theme</Label>
                    <RSelect
                      options={themeOptions}
                      value={themeOptions.find(opt => opt.value === exportConfig.theme)}
                      onChange={(option) => handleConfigChange('theme', option.value)}
                      placeholder="Select theme..."
                    />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>Presentation Style</Label>
                    <RSelect
                      options={presentationStyleOptions}
                      value={presentationStyleOptions.find(opt => opt.value === exportConfig.presentationStyle)}
                      onChange={(option) => handleConfigChange('presentationStyle', option.value)}
                      placeholder="Select style..."
                    />
                  </FormGroup>
                </Col>
              </Row>

              {exportConfig.format === EXPORT_FORMATS.PDF && (
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Page Orientation</Label>
                      <RSelect
                        options={orientationOptions}
                        value={orientationOptions.find(opt => opt.value === exportConfig.orientation)}
                        onChange={(option) => handleConfigChange('orientation', option.value)}
                        placeholder="Select orientation..."
                      />
                    </FormGroup>
                  </Col>
                </Row>
              )}

              <FormGroup>
                <Label>Custom Title (Optional)</Label>
                <Input
                  type="text"
                  value={exportConfig.customTitle}
                  onChange={(e) => handleConfigChange('customTitle', e.target.value)}
                  placeholder="Enter custom report title..."
                />
              </FormGroup>

              {exportConfig.presentationStyle === 'full' && (
                <FormGroup>
                  <Label>Include Sections</Label>
                  <div className="mt-2">
                    <div className="form-check mb-2">
                      <Input
                        type="checkbox"
                        id="includeTopPerformers"
                        checked={exportConfig.includeTopPerformers}
                        onChange={(e) => handleConfigChange('includeTopPerformers', e.target.checked)}
                      />
                      <Label check for="includeTopPerformers" className="ms-2">
                        Top Performers Section
                      </Label>
                    </div>

                    <div className="form-check mb-2">
                      <Input
                        type="checkbox"
                        id="includeFullLeaderboard"
                        checked={exportConfig.includeFullLeaderboard}
                        onChange={(e) => handleConfigChange('includeFullLeaderboard', e.target.checked)}
                      />
                      <Label check for="includeFullLeaderboard" className="ms-2">
                        Full Leaderboard Table
                      </Label>
                    </div>

                    <div className="form-check mb-2">
                      <Input
                        type="checkbox"
                        id="includeCharts"
                        checked={exportConfig.includeCharts}
                        onChange={(e) => handleConfigChange('includeCharts', e.target.checked)}
                      />
                      <Label check for="includeCharts" className="ms-2">
                        Performance Charts
                      </Label>
                    </div>

                    <div className="form-check mb-2">
                      <Input
                        type="checkbox"
                        id="includeTeamPerformance"
                        checked={exportConfig.includeTeamPerformance}
                        onChange={(e) => handleConfigChange('includeTeamPerformance', e.target.checked)}
                      />
                      <Label check for="includeTeamPerformance" className="ms-2">
                        Team Performance
                      </Label>
                    </div>

                    <div className="form-check">
                      <Input
                        type="checkbox"
                        id="includeLeadFunnel"
                        checked={exportConfig.includeLeadFunnel}
                        onChange={(e) => handleConfigChange('includeLeadFunnel', e.target.checked)}
                      />
                      <Label check for="includeLeadFunnel" className="ms-2">
                        Lead Conversion Funnel
                      </Label>
                    </div>
                  </div>
                </FormGroup>
              )}

              {(exportConfig.includeTopPerformers || exportConfig.presentationStyle === 'compact') && (
                <FormGroup>
                  <Label>
                    {exportConfig.presentationStyle === 'compact' ? 'Number of Agents to Show' : 'Number of Top Performers'}
                  </Label>
                  <RSelect
                    options={topCountOptions}
                    value={topCountOptions.find(opt => opt.value === exportConfig.topCount)}
                    onChange={(option) => handleConfigChange('topCount', option.value)}
                    placeholder="Select count..."
                  />
                </FormGroup>
              )}
            </Form>
          </Col>

          {/* Preview Panel */}
          <Col lg="4">
            <Card className="border-0 bg-light">
              <CardBody>
                <h6 className="mb-3">
                  <Icon name="eye" className="me-2" />
                  Export Preview
                </h6>

                <div className="mb-3">
                  <small className="text-muted d-block">Format</small>
                  <Badge color="primary" className="text-uppercase">
                    {exportConfig.format}
                  </Badge>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block">Ranking By</small>
                  <strong>{rankingOption?.label}</strong>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block">Theme</small>
                  <div className="d-flex align-items-center">
                    <div
                      className="me-2 rounded"
                      style={{
                        width: '16px',
                        height: '16px',
                        backgroundColor: theme?.primaryColor
                      }}
                    ></div>
                    <span>{theme?.name}</span>
                  </div>
                </div>

                {exportConfig.includeTopPerformers && (
                  <div className="mb-3">
                    <small className="text-muted d-block">Top Performers</small>
                    <strong>{exportConfig.topCount} agents</strong>
                  </div>
                )}

                <div className="mb-3">
                  <small className="text-muted d-block">Total Agents</small>
                  <strong>{totalAgents} agents</strong>
                </div>

                <div className="text-muted small">
                  <Icon name="info" className="me-1" />
                  Preview will be generated based on current filters and data
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="border-top">
        <Button color="secondary" onClick={toggle} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleExport}
          disabled={isLoading || !agentData.length}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Generating...
            </>
          ) : (
            <>
              <Icon name="download" className="me-2" />
              Export Report
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExportModal;
